import { distancePointToSegment, type Vec2 } from '@indusketch/geometry';

/** Default clear width of a handover gate: a pallet on the forks with room to both sides. */
export const HANDOVER_GATE_WIDTH = 2.5;
/** Railing ends this far from a gate end still count as touching it. */
const ON_LINE = 0.05;

export interface RailingGate {
  /** Edge of the outline (mezzanine) or segment of the polyline (drawn railing). */
  readonly edge: number;
  /** From the edge's start to the gate's start. */
  readonly offset: number;
  readonly width: number;
}

/** Where a gate closes the railing: from `a` to `b`; it slides open towards `b`. */
export interface GateSpan {
  readonly a: Vec2;
  readonly b: Vec2;
}

/** Edges of a polyline, or of a closed outline (`closed`), as start/end pairs. */
export function railEdges(points: readonly Vec2[], closed: boolean): { a: Vec2; b: Vec2 }[] {
  const n = closed ? points.length : points.length - 1;
  return Array.from({ length: Math.max(0, n) }, (_, i) => ({
    a: points[i] as Vec2,
    b: points[(i + 1) % points.length] as Vec2,
  }));
}

/** The gates placed on their edges (clamped to the edge; gates on missing edges left out). */
export function gateSpans(
  points: readonly Vec2[],
  closed: boolean,
  gates: readonly RailingGate[] = [],
): GateSpan[] {
  const edges = railEdges(points, closed);
  return gates.flatMap((g) => {
    const e = edges[g.edge];
    if (!e) return [];
    const len = Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y);
    if (len < 1e-6) return [];
    const width = Math.min(g.width, len);
    const start = Math.max(0, Math.min(g.offset, len - width));
    const at = (s: number) => ({
      x: e.a.x + ((e.b.x - e.a.x) * s) / len,
      y: e.a.y + ((e.b.y - e.a.y) * s) / len,
    });
    return [{ a: at(start), b: at(start + width) }];
  });
}

/** Handover gates of a mezzanine railing. */
export const mezzanineGateSpans = (m: {
  readonly polygon: readonly Vec2[];
  readonly railingGates?: readonly RailingGate[] | undefined;
}): GateSpan[] => gateSpans(m.polygon, true, m.railingGates);

/** Handover gates of a drawn railing. */
export const railingGateSpans = (r: {
  readonly points: readonly Vec2[];
  readonly gates?: readonly RailingGate[] | undefined;
}): GateSpan[] => gateSpans(r.points, false, r.gates);

/** Runs of a drawn railing between its points, with its handover gates left open. */
export const railingRuns = (r: {
  readonly points: readonly Vec2[];
  readonly gates?: readonly RailingGate[] | undefined;
}): { a: Vec2; b: Vec2 }[] => cutAtGates(railEdges(r.points, false), railingGateSpans(r));

/** Railing runs with the gates cut out, so the railing stops at both sides of each gate. */
export function cutAtGates(
  runs: readonly { readonly a: Vec2; readonly b: Vec2 }[],
  spans: readonly GateSpan[],
): { a: Vec2; b: Vec2 }[] {
  let out = runs.map((r) => ({ a: r.a, b: r.b }));
  for (const g of spans) {
    out = out.flatMap((r) => {
      const len = Math.hypot(r.b.x - r.a.x, r.b.y - r.a.y);
      if (
        len < 1e-6 ||
        distancePointToSegment(g.a, r) > ON_LINE ||
        distancePointToSegment(g.b, r) > ON_LINE
      )
        return [r];
      const along = (p: Vec2) =>
        ((p.x - r.a.x) * (r.b.x - r.a.x) + (p.y - r.a.y) * (r.b.y - r.a.y)) / len;
      const [s0, s1] = [along(g.a), along(g.b)].sort((p, q) => p - q) as [number, number];
      const at = (s: number) => ({
        x: r.a.x + ((r.b.x - r.a.x) * s) / len,
        y: r.a.y + ((r.b.y - r.a.y) * s) / len,
      });
      return [
        ...(s0 > ON_LINE ? [{ a: r.a, b: at(s0) }] : []),
        ...(len - s1 > ON_LINE ? [{ a: at(s1), b: r.b }] : []),
      ];
    });
  }
  return out;
}
